import { isApiError } from '../../api/client'
import { Button } from '../../design'
import { NOTIFICATION_COPY } from './notificationCopy'
import { PushSetupError, pushSupported, type PushSetupFailure } from './pushBrowser'
import { useDisablePush, useEnablePush, usePushEnabled, usePushPublicKey } from './pushQueries'

const SETUP_MESSAGES: Record<PushSetupFailure, string> = {
  denied: NOTIFICATION_COPY.pushDenied,
  not_ready: NOTIFICATION_COPY.pushNotReady,
  service: NOTIFICATION_COPY.pushServiceDown,
  invalid: NOTIFICATION_COPY.pushFailed,
}

const messageFor = (error: unknown): string | null => {
  if (error === null) return null
  if (error instanceof PushSetupError) return SETUP_MESSAGES[error.reason]
  return isApiError(error) ? error.message : NOTIFICATION_COPY.pushFailed
}

const PushControls = ({ publicKey }: { publicKey: string }) => {
  const enabled = usePushEnabled()
  const enable = useEnablePush()
  const disable = useDisablePush()
  const error = messageFor(enable.error ?? disable.error ?? enabled.error)

  if (enabled.isPending) return <p className="settings-hint">{NOTIFICATION_COPY.loading}</p>

  return (
    <>
      {error === null ? null : (
        <p className="settings-error text-tone tone-danger" role="alert">
          {error}
        </p>
      )}
      {enabled.data === true ? (
        <>
          <p className="notification-state">{NOTIFICATION_COPY.pushOn}</p>
          <Button pending={disable.isPending} pendingLabel={NOTIFICATION_COPY.pushDisabling} onClick={() => disable.mutate()}>
            {NOTIFICATION_COPY.pushDisable}
          </Button>
        </>
      ) : (
        <Button
          variant="primary"
          pending={enable.isPending}
          pendingLabel={NOTIFICATION_COPY.pushEnabling}
          onClick={() => enable.mutate(publicKey)}
        >
          {NOTIFICATION_COPY.pushEnable}
        </Button>
      )}
    </>
  )
}

const PushAvailability = () => {
  const publicKey = usePushPublicKey()
  if (publicKey.isPending) return <p className="settings-hint">{NOTIFICATION_COPY.loading}</p>
  if (publicKey.isError) {
    return (
      <p className="settings-error text-tone tone-danger" role="alert">
        {messageFor(publicKey.error)}
      </p>
    )
  }
  if (publicKey.data === null) return <p className="notification-state">{NOTIFICATION_COPY.pushUnavailable}</p>
  return <PushControls publicKey={publicKey.data} />
}

export const PushSetting = () => (
  <div className="notification-setting">
    <h3>{NOTIFICATION_COPY.pushTitle}</h3>
    <p className="settings-hint measure">{NOTIFICATION_COPY.pushHint}</p>
    {pushSupported() ? <PushAvailability /> : <p className="notification-state">{NOTIFICATION_COPY.pushUnsupported}</p>}
  </div>
)
